import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  StopOutlined,
  TableOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import React, { FC, useState } from 'react';

import { useOperationsDashboardContext } from '../../contexts/OperationsDashboardContext';
import styles from '../OperationsDashboard/ops-theme.module.css';
import OpsMetricDetailModal, {
  IOpsMetricDetailConfig,
} from '../OpsMetricDetailModal';

export interface IOpsDonorPipelineProps {
  locationId: string;
  activeDonors: number;
  dblDonorCount: number;
  sglDonorCount: number;
  sglPercent?: number;
  firstTimeDonationCount: number;
  recruited: number;
  retiredDonorCount: number;
  periodLabel: string;
  unitsPerYear?: number;
  retiringIn30Count?: number;
  retiringIn60Count?: number;
  retiringIn90Count?: number;
  avgDaysAppliedToActive?: number;
  avgDaysPendingToApproved?: number;
  avgDaysActiveToRetired?: number;
  avgDaysPendingToArchived?: number;
  onDrilldown?: (metricKey: string) => void;
}

function getSglStatus(sglPercent?: number): 'up' | 'warn' | 'down' {
  if (sglPercent == null) return 'up';
  if (sglPercent >= 28) return 'down';
  if (sglPercent >= 23) return 'warn';
  return 'up';
}

const SGL_BAR_CLASS: Record<string, string> = {
  up: styles.pipelineBarFillUp,
  warn: styles.pipelineBarFillWarn,
  down: styles.pipelineBarFillDown,
};

const SGL_PCT_CLASS: Record<string, string> = {
  up: styles.statusUp,
  warn: styles.statusWarn,
  down: styles.statusDown,
};

function formatDays(value?: number): string {
  if (value == null) return '0d';
  const days = Math.round(value);
  if (days >= 365) {
    const years = Math.floor(days / 365);
    const remainder = days % 365;
    return remainder > 0 ? `${years}y${remainder}d` : `${years}y`;
  }
  return `${days}d`;
}

const OpsDonorPipelineCards: FC<IOpsDonorPipelineProps> = ({
  locationId,
  activeDonors,
  dblDonorCount,
  sglDonorCount,
  sglPercent,
  firstTimeDonationCount,
  recruited,
  retiredDonorCount,
  periodLabel,
  unitsPerYear,
  retiringIn30Count,
  retiringIn60Count,
  retiringIn90Count,
  avgDaysAppliedToActive,
  avgDaysPendingToApproved,
  avgDaysActiveToRetired,
  avgDaysPendingToArchived,
  onDrilldown,
}) => {
  const { isDarkMode } = useOperationsDashboardContext();
  const [modalConfig, setModalConfig] = useState<IOpsMetricDetailConfig | null>(
    null
  );

  const sglPct =
    activeDonors > 0 ? Math.round((sglDonorCount / activeDonors) * 100) : 0;
  const dblPct = activeDonors > 0 ? 100 - sglPct : 0;
  const sglStatus = getSglStatus(sglPercent);

  const retiringTotal =
    (retiringIn30Count ?? 0) +
    (retiringIn60Count ?? 0) +
    (retiringIn90Count ?? 0);

  const openModal = (config: IOpsMetricDetailConfig, e: React.MouseEvent) => {
    e.stopPropagation();
    setModalConfig(config);
  };

  return (
    <>
      <div className={styles.pipelineSection}>
        {/* Left column */}
        <div className={styles.pipelineCol}>
          <div
            className={styles.pipelineInfoTabH}
            onClick={(e) =>
              openModal(
                {
                  title: 'Active Donors — SGL / DBL',
                  description: (
                    <>
                      <strong>Active Donors — SGL / DBL</strong> breaks down the
                      active donor pool by donation type.{' '}
                      <strong>DBL (double unit)</strong> donors contribute two
                      pRBC units per session, doubling their output vs. SGL. A
                      high SGL% significantly reduces collection efficiency —
                      the target is to keep SGL below 23% of the active pool.
                    </>
                  ),
                  rows: [
                    {
                      label: 'Total Active Donors',
                      value: activeDonors,
                      bold: true,
                    },
                    {
                      label: 'Double Unit (DBL)',
                      value: dblDonorCount,
                      indent: true,
                      color: '#4B9EFF',
                    },
                    {
                      label: 'DBL %',
                      value: `${dblPct}%`,
                      indent: true,
                      color: '#4B9EFF',
                    },
                    {
                      label: 'Single Unit (SGL)',
                      value: sglDonorCount,
                      indent: true,
                      color:
                        sglStatus === 'down'
                          ? '#E8008A'
                          : sglStatus === 'warn'
                          ? '#c6c300'
                          : '#00E5A0',
                    },
                    {
                      label: 'SGL %',
                      value: `${sglPct}%`,
                      indent: true,
                      bold: true,
                      color:
                        sglStatus === 'down'
                          ? '#E8008A'
                          : sglStatus === 'warn'
                          ? '#c6c300'
                          : '#00E5A0',
                    },
                    {
                      label: '1st Time Donations',
                      value: firstTimeDonationCount,
                    },
                  ],
                  formula:
                    'SGL% = SGL Donors ÷ Active Donors × 100  |  Target: < 23%',
                },
                e
              )
            }
          >
            <InfoCircleOutlined />
          </div>

          {/* Row 1: Active Donors — SGL / DBL */}
          <div>
            <span className={styles.pipelineRowHeading}>
              Active Donors — SGL / DBL
            </span>
            <table className={styles.pipelineTable}>
              <tbody>
                {/* Double Unit row */}
                <tr>
                  <td className={styles.pipelineTableLabel}>
                    Double Unit (DBL)
                  </td>
                  <td className={styles.pipelineTableCount}>
                    <span className={styles.statusInfo}>{dblDonorCount}</span>
                  </td>
                  <td className={styles.pipelineBarCell}>
                    <div className={styles.pipelineBar}>
                      <div className={styles.pipelineBarTrack}>
                        <div
                          className={`${styles.pipelineBarFill} ${styles.pipelineBarFillInfo}`}
                          style={{ width: `${Math.min(dblPct, 100)}%` }}
                        />
                      </div>
                      <span
                        className={`${styles.pipelineBarPct} ${styles.statusInfo}`}
                      >
                        {dblPct}%
                      </span>
                    </div>
                  </td>
                </tr>
                {/* Single Unit row */}
                <tr>
                  <td className={styles.pipelineTableLabel}>
                    Single Unit (SGL)
                  </td>
                  <td className={styles.pipelineTableCount}>
                    <span
                      className={
                        sglStatus === 'down'
                          ? styles.statusDown
                          : sglStatus === 'warn'
                          ? styles.statusWarn
                          : styles.statusUp
                      }
                    >
                      {sglDonorCount}
                    </span>
                  </td>
                  <td className={styles.pipelineBarCell}>
                    <div className={styles.pipelineBar}>
                      <div className={styles.pipelineBarTrack}>
                        <div
                          className={`${styles.pipelineBarFill} ${SGL_BAR_CLASS[sglStatus]}`}
                          style={{ width: `${Math.min(sglPct, 100)}%` }}
                        />
                      </div>
                      <span
                        className={`${styles.pipelineBarPct} ${SGL_PCT_CLASS[sglStatus]}`}
                      >
                        {sglPct}%
                        {sglStatus !== 'up' && (
                          <>
                            {' '}
                            <WarningOutlined style={{ fontSize: 10 }} />
                          </>
                        )}
                      </span>
                    </div>
                  </td>
                </tr>
                {/* First-Time Donations row */}
                <tr>
                  <td className={styles.pipelineTableLabel}>
                    1st Time Donations
                  </td>
                  <td className={styles.pipelineTableCount} colSpan={2}>
                    {firstTimeDonationCount}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Row 2: Donor Churn */}
          <div>
            <span className={styles.pipelineRowHeading}>Donor Churn</span>
            <div className={styles.pipelineChurnRow}>
              <div className={styles.pipelineChurnCard}>
                <div
                  className={styles.summaryCardInfoTab}
                  onClick={(e) =>
                    openModal(
                      {
                        title: 'New Recruits',
                        description: (
                          <>
                            <strong>New Recruits</strong> counts donors who were
                            registered at this location for the first time
                            during the selected period. Consistent recruiting is
                            essential to offset natural attrition and grow the
                            active donor base over time.
                          </>
                        ),
                        rows: [
                          {
                            label: `New Recruits (${periodLabel})`,
                            value: recruited,
                            bold: true,
                            color: '#E8008A',
                          },
                        ],
                        formula: `Count of donors with registration date within the ${periodLabel} period`,
                      },
                      e
                    )
                  }
                >
                  <InfoCircleOutlined />
                </div>
                <span className={styles.pipelineChurnLabel}>New Recruits</span>
                <div className={styles.healthFlagCardValueRow}>
                  <span className={styles.pipelineChurnValue}>{recruited}</span>
                  <button
                    type="button"
                    className={styles.healthFlagDrilldownBtn}
                    data-drilldown-id={`${locationId}:newRecruits`}
                    title="View in Drilldown"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDrilldown?.('newRecruits');
                    }}
                  >
                    <TableOutlined />
                  </button>
                </div>
                <span className={styles.pipelineChurnSub}>{periodLabel}</span>
              </div>
              <div className={styles.pipelineChurnCard}>
                <div
                  className={styles.summaryCardInfoTab}
                  onClick={(e) =>
                    openModal(
                      {
                        title: 'Retired Donors',
                        description: (
                          <>
                            <strong>Retired Donors</strong> counts donors whose
                            status changed to Retired during the selected
                            period. Retirement is typically age-related.
                            Tracking this against new recruits gives a net churn
                            view — if retirements consistently outpace
                            recruiting, the active pool will shrink.
                          </>
                        ),
                        rows: [
                          {
                            label: `Retired (${periodLabel})`,
                            value: retiredDonorCount,
                            bold: true,
                            color: '#9B8DB8',
                          },
                        ],
                        formula: `Count of donors whose status changed to Retired within the ${periodLabel} period`,
                      },
                      e
                    )
                  }
                >
                  <InfoCircleOutlined />
                </div>
                <span className={styles.pipelineChurnLabel}>Retired</span>
                <div className={styles.healthFlagCardValueRow}>
                  <span className={styles.pipelineChurnValue}>
                    {retiredDonorCount}
                  </span>
                  <button
                    type="button"
                    className={styles.healthFlagDrilldownBtn}
                    data-drilldown-id={`${locationId}:retired`}
                    title="View in Drilldown"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDrilldown?.('retired');
                    }}
                  >
                    <TableOutlined />
                  </button>
                </div>
                <span className={styles.pipelineChurnSub}>{periodLabel}</span>
              </div>
            </div>
          </div>

          {/* Row 3: Avg # Units / Year */}
          <div className={styles.pipelineUnitsCard}>
            <div
              className={styles.summaryCardInfoTab}
              onClick={(e) =>
                openModal(
                  {
                    title: 'Avg # Units / Year',
                    description: (
                      <>
                        <strong>Avg # Units / Year</strong> estimates the
                        annualized pRBC collection rate at this location based
                        on all historical donation data. It normalizes for
                        location age, making it useful for comparing
                        productivity across sites with different tenures.
                      </>
                    ),
                    rows: [
                      {
                        label: 'Avg Units / Year',
                        value:
                          unitsPerYear != null ? Math.round(unitsPerYear) : '—',
                        bold: true,
                        color: '#E8008A',
                      },
                    ],
                    formula:
                      'Avg Units/Year = Total Units Since Inception ÷ Days Since First Donation × 365',
                  },
                  e
                )
              }
            >
              <InfoCircleOutlined />
            </div>
            <span className={styles.pipelineChurnLabel}>
              Avg # Units / Year
            </span>
            <span className={styles.pipelineChurnValue}>
              {unitsPerYear != null ? Math.round(unitsPerYear) : '—'}
            </span>
          </div>
        </div>

        {/* Right column */}
        <div className={styles.pipelineCol}>
          <div
            className={styles.pipelineInfoTabH}
            onClick={(e) =>
              openModal(
                {
                  title: 'Retiring Donors — Due to Age',
                  description: (
                    <>
                      <strong>Retiring Donors</strong> shows active donors who
                      will age out of donation eligibility within the next 30,
                      60, and 90 days. Each retiring donor must be replaced to
                      maintain collection volume. High counts in the 30-day
                      bucket require immediate recruiting action.
                    </>
                  ),
                  rows: [
                    { label: 'Total Active Donors', value: activeDonors },
                    {
                      label: 'Retiring in 30 days',
                      value: retiringIn30Count ?? 0,
                      bold: true,
                      color: '#E8008A',
                    },
                    {
                      label: 'Retiring in 60 days',
                      value: retiringIn60Count ?? 0,
                      bold: true,
                      color: '#c6c300',
                    },
                    {
                      label: 'Retiring in 90 days',
                      value: retiringIn90Count ?? 0,
                      bold: true,
                      color: '#00E5A0',
                    },
                    {
                      label: 'Total Retiring (90d)',
                      value: retiringTotal,
                      bold: true,
                    },
                    {
                      label: '% of Active Donors',
                      value:
                        activeDonors > 0
                          ? `${Math.round(
                              (retiringTotal / activeDonors) * 100
                            )}%`
                          : '—',
                      color:
                        retiringTotal / Math.max(activeDonors, 1) > 0.1
                          ? '#E8008A'
                          : '#00E5A0',
                    },
                  ],
                  formula:
                    'Eligibility cutoff based on donor age — donors exceeding the maximum age threshold',
                },
                e
              )
            }
          >
            <InfoCircleOutlined />
          </div>

          {/* Row 1: Retiring Donors */}
          <div>
            <span className={styles.pipelineRowHeading}>
              Retiring Donors [Due to Age]
            </span>
            <table className={styles.pipelineTable}>
              <tbody>
                <tr
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDrilldown?.('retiringIn30');
                  }}
                >
                  <td className={styles.pipelineTableLabel}>
                    Retiring in 30 days
                  </td>
                  <td className={styles.pipelineTableCount}>
                    <span
                      className={`${styles.pipelineCountBadge} ${styles.pipelineCountBadgeSm} ${styles.pipelineCountBadgeDown}`}
                    >
                      <TeamOutlined className={styles.pipelineCountBadgeIcon} />
                      {retiringIn30Count ?? 0}
                    </span>
                  </td>
                </tr>
                <tr
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDrilldown?.('retiringIn60');
                  }}
                >
                  <td className={styles.pipelineTableLabel}>
                    Retiring in 60 days
                  </td>
                  <td className={styles.pipelineTableCount}>
                    <span
                      className={`${styles.pipelineCountBadge} ${styles.pipelineCountBadgeSm} ${styles.pipelineCountBadgeWarn}`}
                    >
                      <TeamOutlined className={styles.pipelineCountBadgeIcon} />
                      {retiringIn60Count ?? 0}
                    </span>
                  </td>
                </tr>
                <tr
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDrilldown?.('retiringIn90');
                  }}
                >
                  <td className={styles.pipelineTableLabel}>
                    Retiring in 90 days
                  </td>
                  <td className={styles.pipelineTableCount}>
                    <span
                      className={`${styles.pipelineCountBadge} ${styles.pipelineCountBadgeSm} ${styles.pipelineCountBadgeUp}`}
                    >
                      <TeamOutlined className={styles.pipelineCountBadgeIcon} />
                      {retiringIn90Count ?? 0}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            <span className={styles.pipelineRetiringSubtext}>
              <span className={styles.statusDown}>//</span> Out of{' '}
              <span className={styles.statusInfo}>{activeDonors}</span> Active
              Donors
            </span>
          </div>

          {/* Row 2: Pipeline Timing Averages */}
          <div>
            <span className={styles.pipelineRowHeading}>
              Pipeline Timing Averages (by status)
            </span>
            <div className={styles.pipelineTimingRow}>
              <div className={styles.pipelineTimingCard}>
                <div
                  className={styles.summaryCardInfoTab}
                  onClick={(e) =>
                    openModal(
                      {
                        title: 'Applied → Active Pipeline',
                        description: (
                          <>
                            <strong>Applied → Active</strong> measures the
                            average number of days donors spent in Applied
                            status before transitioning to Active. Shorter
                            durations indicate an efficient onboarding process.
                            Long delays often signal scheduling friction or
                            incomplete intake steps.
                          </>
                        ),
                        rows: [
                          {
                            label: 'Avg Days (Applied → Active)',
                            value: formatDays(avgDaysAppliedToActive),
                            bold: true,
                            color: '#4B9EFF',
                          },
                        ],
                        formula:
                          'AVG(date_status_active - date_status_applied) for donors who transitioned during the period',
                      },
                      e
                    )
                  }
                >
                  <InfoCircleOutlined />
                </div>
                <span className={styles.pipelineTimingTransition}>
                  <span className={styles.pipelineTimingLabel}>Applied</span>
                  <ArrowRightOutlined className={styles.pipelineTimingArrow} />
                  <span className={styles.pipelineTimingLabel}>Active</span>
                </span>
                <span
                  className={`${styles.pipelineCountBadge} ${styles.pipelineCountBadgeInfo}`}
                  style={{
                    cursor: avgDaysAppliedToActive ? 'pointer' : 'default',
                    opacity: avgDaysAppliedToActive ? 1 : 0.5,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (avgDaysAppliedToActive)
                      onDrilldown?.('appliedToActive');
                  }}
                >
                  <ThunderboltOutlined
                    className={styles.pipelineCountBadgeIcon}
                  />
                  {avgDaysAppliedToActive == null
                    ? 'No Status History'
                    : formatDays(avgDaysAppliedToActive)}
                </span>
              </div>
              <div className={styles.pipelineTimingCard}>
                <div
                  className={styles.summaryCardInfoTab}
                  onClick={(e) =>
                    openModal(
                      {
                        title: 'Pending → Approved Pipeline',
                        description: (
                          <>
                            <strong>Pending → Approved</strong> tracks the
                            average days between a donor entering Pending status
                            and being approved for active donation. High values
                            indicate a review or communication bottleneck.
                            Reducing this time directly improves pipeline
                            conversion speed.
                          </>
                        ),
                        rows: [
                          {
                            label: 'Avg Days (Pending → Approved)',
                            value: formatDays(avgDaysPendingToApproved),
                            bold: true,
                            color: '#4B9EFF',
                          },
                        ],
                        formula:
                          'AVG(date_status_approved - date_status_pending) for donors who transitioned during the period',
                      },
                      e
                    )
                  }
                >
                  <InfoCircleOutlined />
                </div>
                <span className={styles.pipelineTimingTransition}>
                  <span className={styles.pipelineTimingLabel}>Pending</span>
                  <ArrowRightOutlined className={styles.pipelineTimingArrow} />
                  <span className={styles.pipelineTimingLabel}>Approved</span>
                </span>
                <span
                  className={`${styles.pipelineCountBadge} ${styles.pipelineCountBadgeInfo}`}
                  style={{
                    cursor: avgDaysPendingToApproved ? 'pointer' : 'default',
                    opacity: avgDaysPendingToApproved ? 1 : 0.5,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (avgDaysPendingToApproved)
                      onDrilldown?.('pendingToApproved');
                  }}
                >
                  <CheckCircleOutlined
                    className={styles.pipelineCountBadgeIcon}
                  />
                  {formatDays(avgDaysPendingToApproved)}
                </span>
              </div>
              <div className={styles.pipelineTimingCard}>
                <div
                  className={styles.summaryCardInfoTab}
                  onClick={(e) =>
                    openModal(
                      {
                        title: 'Active → Retired Pipeline',
                        description: (
                          <>
                            <strong>Active → Retired</strong> reflects the
                            average active donor tenure — how long donors
                            typically remain active before retiring. Longer
                            tenure means greater lifetime value per donor. Short
                            tenures at a location may indicate scheduling
                            friction or poor retention practices.
                          </>
                        ),
                        rows: [
                          {
                            label: 'Avg Days (Active → Retired)',
                            value: formatDays(avgDaysActiveToRetired),
                            bold: true,
                            color: '#4B9EFF',
                          },
                          {
                            label: 'Avg Years Active',
                            value:
                              avgDaysActiveToRetired != null
                                ? `${(avgDaysActiveToRetired / 365).toFixed(
                                    1
                                  )} yrs`
                                : '—',
                          },
                        ],
                        formula:
                          'AVG(date_status_retired - date_status_active) for donors who retired during the period',
                      },
                      e
                    )
                  }
                >
                  <InfoCircleOutlined />
                </div>
                <span className={styles.pipelineTimingTransition}>
                  <span className={styles.pipelineTimingLabel}>Active</span>
                  <ArrowRightOutlined className={styles.pipelineTimingArrow} />
                  <span className={styles.pipelineTimingLabel}>Retired</span>
                </span>
                <span
                  className={`${styles.pipelineCountBadge} ${styles.pipelineCountBadgeInfo}`}
                  style={{
                    cursor: avgDaysActiveToRetired ? 'pointer' : 'default',
                    opacity: avgDaysActiveToRetired ? 1 : 0.5,
                    background: '#e91e63',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (avgDaysActiveToRetired)
                      onDrilldown?.('activeToRetired');
                  }}
                >
                  <StopOutlined className={styles.pipelineCountBadgeIcon} />
                  {formatDays(avgDaysActiveToRetired)}
                </span>
              </div>
              <div className={styles.pipelineTimingCard}>
                <div
                  className={styles.summaryCardInfoTab}
                  onClick={(e) =>
                    openModal(
                      {
                        title: 'Pending → Archived Pipeline',
                        description: (
                          <>
                            <strong>Pending → Archived</strong> measures the
                            average time donors spent in Pending before being
                            archived without ever progressing to Active. These
                            are lost pipeline opportunities. High values or high
                            counts suggest donors are disengaging before
                            completing the approval process.
                          </>
                        ),
                        rows: [
                          {
                            label: 'Avg Days (Pending → Archived)',
                            value: formatDays(avgDaysPendingToArchived),
                            bold: true,
                            color: '#9B8DB8',
                          },
                        ],
                        formula:
                          'AVG(date_status_archived - date_status_pending) for donors who were archived during the period',
                      },
                      e
                    )
                  }
                >
                  <InfoCircleOutlined />
                </div>
                <span className={styles.pipelineTimingTransition}>
                  <span className={styles.pipelineTimingLabel}>Pending</span>
                  <ArrowRightOutlined className={styles.pipelineTimingArrow} />
                  <span className={styles.pipelineTimingLabel}>Archived</span>
                </span>
                <span
                  className={`${styles.pipelineCountBadge} ${styles.pipelineCountBadgeInfo}`}
                  style={{
                    cursor: avgDaysPendingToArchived ? 'pointer' : 'default',
                    opacity: avgDaysPendingToArchived ? 1 : 0.5,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (avgDaysPendingToArchived)
                      onDrilldown?.('pendingToArchived');
                  }}
                >
                  <InboxOutlined className={styles.pipelineCountBadgeIcon} />
                  {formatDays(avgDaysPendingToArchived)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <OpsMetricDetailModal
        visible={modalConfig !== null}
        onClose={() => setModalConfig(null)}
        config={modalConfig}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default OpsDonorPipelineCards;
